import {
  Asset,
  DDIProvision,
  PortStatus,
  PortSubmission,
  Product,
  ServiceCharge,
  ServiceChargePost,
} from "callable-data-objects";
import useCheckRole from "Hooks/APICheckRole";
import { useTicket } from "Hooks/Endpoints";
import { isEmpty } from "lodash";
import moment from "moment";
import React, { Fragment, useState } from "react";
import FileViewer from "react-file-viewer";
import {
  Button,
  Checkbox,
  Dimmer,
  Label,
  Loader,
  Menu,
  Segment,
  Table,
} from "semantic-ui-react";
import PortSubmissionForm from "./PortSubmissionForm";

const PortingBuildForm = () => {
  const {
    ticket,
    loading,
    charges,
    ticketProductTable,
    addCharge,
    addCharges,
    removeCharge,
    buildPorting,
  } = useTicket();

  const { hasRole, roles } = useCheckRole();

  const [assetView, setAssetView] = useState(0);

  const { customer, submissions, ddis, assets } = ticket.portRequest;

  const getCharge = (s: string): ServiceCharge => {
    const p = getProduct(s);
    return (
      p &&
      charges &&
      charges.find(
        (sc: ServiceCharge) => sc.reference === s && sc.name === p.name
      )
    );
  };

  const submissionNumbers = (
    submission: PortSubmission,
    includeSubs: Boolean
  ): string[] => {
    const nums: string[] = [];
    const { mbn, associatedNumbers, rangeNumbers } = submission;
    if (!isEmpty(mbn)) nums.push(cleanDDI(mbn));
    if (!isEmpty(associatedNumbers) && includeSubs)
      associatedNumbers
        .split("\n")
        .map((s) => cleanDDI(s))
        .forEach((s: string) => {
          nums.push(s);
        });
    if (!isEmpty(rangeNumbers) && includeSubs)
      rangeNumbers
        .split("\n")
        .map((s) => cleanDDI(s))
        .forEach((s: string) => {
          nums.push(s);
        });
    return nums;
  };

  const getProduct = (s: string) => {
    const productName: string = submissions.find(
      (sub: PortSubmission) =>
        (getLastStatus(sub.statuses) === "provisioned" ||
          getLastStatus(sub.statuses) === "activated") &&
        submissionNumbers(sub, false).indexOf(s) > -1
    )?.lineType;
    return (
      productName &&
      ticketProductTable &&
      ticketProductTable.products.find((pr: Product) => productName === pr.name)
    );
  };

  const getLastStatus = (statuses: PortStatus[]) => {
    if (statuses && statuses.length > 0)
      return statuses[statuses.length - 1].status;
    return "";
  };

  const getSubmissionNumbers = (): string[] => {
    if (!submissions) return [];
    const ddis: string[] = [];
    submissions.forEach((sub: PortSubmission) => {
      if (
        getLastStatus(sub.statuses) === "provisioned" ||
        getLastStatus(sub.statuses) === "activated"
      ) {
        submissionNumbers(sub, true)
          .map((s) => cleanDDI(s))
          .forEach((s) => {
            if (!isEmpty(s) && s !== "null" && ddis.indexOf(s) === -1) {
              ddis.push(s);
            }
          });
      }
    });
    return ddis;
  };

  const cleanDDI = (s: string) => {
    if (s.startsWith("00")) {
      s = s.substring(2);
    }
    if (s.startsWith("0")) {
      s = "44" + s.substring(1);
    }
    return s;
  };

  async function add(s: string) {
    const product = getProduct(s);
    if (product) {
      const charge: ServiceChargePost = {
        customer: customer,
        reference: s,
        name: product.name,
        type: product.category,
        description: product.description,
        ticketReference: ticket.caseId,
        unit: product.type,
        price: product.price,
        qty: 1,
        total: product.price,
        start: moment().toISOString(),
      };
      await addCharge(charge);
    }
  }

  const remove = (s: string) => {
    const c: ServiceCharge = getCharge(s);
    if (c) {
      removeCharge(c.id);
    }
  };

  async function buildCharges() {
    const charges = getSubmissionNumbers()
      .map((s: string) => cleanDDI(s))
      .filter((s: string) => getProduct(s))
      .map((s: string) => [getProduct(s), s])
      .map((result: any) => {
        const [product, reference] = result;
        if (product) {
          return {
            customer: customer,
            reference: reference,
            name: product.name,
            type: product.category,
            description: product.description,
            ticketReference: ticket.caseId,
            unit: product.type,
            price: product.price,
            qty: 1,
            total: product.price,
            start: moment().toISOString(),
          };
        }
      });
    await addCharges(charges);
  }

  return (
    <Fragment>
      <Dimmer inverted active={loading}>
        <Loader inverted />
      </Dimmer>

      {hasRole(roles.ticket.submissions.view) && (
        <>
          <h2>Submissions</h2>
          <PortSubmissionForm />
        </>
      )}

      {hasRole(roles.ticket.build.view) && (
        <>
          <br />
          <h2>Provisioning</h2>
          <Segment color='blue'>
            <Menu>
              <Menu.Item header>Provisioning</Menu.Item>
              {hasRole(roles.ticket.build.build) && (
                <Menu.Menu position='right'>
                  <Menu.Item>
                    <Button secondary onClick={buildPorting}>
                      Build
                    </Button>
                  </Menu.Item>
                </Menu.Menu>
              )}
            </Menu>
            <Table compact stackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>DDI</Table.HeaderCell>
                  <Table.HeaderCell>Built</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {getSubmissionNumbers().map((s: string) => (
                  <Table.Row key={`build${s}`}>
                    <Table.Cell width={14}>{s}</Table.Cell>
                    <Table.Cell width={2}>
                      <Checkbox
                        toggle
                        checked={
                          ddis &&
                          ddis.find((dp: DDIProvision) => dp.ddi.name === s) !==
                            undefined
                        }
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        </>
      )}

      {hasRole(roles.ticket.billing.view) && (
        <>
          <br />
          <h2>Billing</h2>
          <Segment color='purple'>
            <Menu>
              <Menu.Item header>
                Billing <Label as='tag'>{ticketProductTable?.name}</Label>
              </Menu.Item>
              {hasRole(roles.ticket.billing.build) && (
                <Menu.Menu position='right'>
                  <Menu.Item>
                    <Button secondary onClick={buildCharges}>
                      Add All
                    </Button>
                  </Menu.Item>
                </Menu.Menu>
              )}
            </Menu>

            <Table compact stackable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>DDI</Table.HeaderCell>
                  <Table.HeaderCell>Product Name</Table.HeaderCell>
                  <Table.HeaderCell>Product Price</Table.HeaderCell>
                  <Table.HeaderCell>Actions</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {getSubmissionNumbers()
                  .map((s: string) => cleanDDI(s))
                  .filter((s: string) => getProduct(s))
                  .map((s: string) => (
                    <Table.Row key={`billing${s}`}>
                      <Table.Cell width={6}>{s}</Table.Cell>
                      <Table.Cell width={6}>{getProduct(s).name}</Table.Cell>
                      <Table.Cell>{getProduct(s).price}</Table.Cell>
                      <Table.Cell>
                        {getCharge(s) === undefined ? (
                          <Button secondary onClick={() => add(s)}>
                            Add
                          </Button>
                        ) : (
                          <Button primary onClick={() => remove(s)}>
                            Remove
                          </Button>
                        )}
                      </Table.Cell>
                    </Table.Row>
                  ))}
              </Table.Body>
            </Table>
          </Segment>
        </>
      )}

      {hasRole(roles.ticket.asset.view) && (
        <>
          <br />
          <h2>Assets</h2>

          <Segment color='orange'>
            <Menu pointing>
              {assets.map((a: Asset, i: number) => (
                <Menu.Item
                  onClick={() => setAssetView(i)}
                  link
                  active={assetView === i}
                >
                  Asset {i + 1}
                </Menu.Item>
              ))}
              {hasRole(roles.ticket.asset.download) && (
                <Menu.Menu position='right'>
                  <Menu.Item>
                    <a
                      download={assets[assetView].name}
                      target='_blank'
                      style={{ display: "block" }}
                      href={assets[assetView].publicURL}
                      rel='noreferrer'
                    >
                      Download Asset {assetView + 1}
                    </a>
                  </Menu.Item>
                </Menu.Menu>
              )}
            </Menu>

            {assets && assets.length > 0 && (
              <Segment style={{ height: "600px" }}>
                <FileViewer
                  fileType={assets[assetView].mimeType.split("/")[1]}
                  filePath={assets[assetView].publicURL}
                />
                <br />
              </Segment>
            )}
          </Segment>
        </>
      )}
    </Fragment>
  );
};

export default PortingBuildForm;
